import Layout from "../components/Layout";
import { Shield } from "lucide-react";
import { motion } from "motion/react";

interface Section {
  id: string;
  heading: string;
  content: React.ReactNode;
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

function PolicyList({ items }: { items: string[] }) {
  return (
    <ul className="mt-3 space-y-2">
      {items.map((item) => (
        <li
          key={item}
          className="flex items-start gap-2.5 text-muted-foreground font-body text-base leading-relaxed"
        >
          <span className="mt-1.5 w-2 h-2 rounded-full bg-gold flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  );
}

function PolicyParagraph({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-muted-foreground font-body text-base leading-relaxed mt-3">
      {children}
    </p>
  );
}

const sections: Section[] = [
  {
    id: "info-collect",
    heading: "Information We Collect",
    content: (
      <>
        <PolicyParagraph>
          When you visit our website or submit a form, you may be asked to
          provide personal information, including:
        </PolicyParagraph>
        <PolicyList items={["Name", "Email address", "Phone number"]} />
        <PolicyParagraph>
          We may also automatically collect limited technical information, such
          as:
        </PolicyParagraph>
        <PolicyList
          items={[
            "IP address",
            "Browser type",
            "Device information",
            "Access times",
            "Referring website addresses",
          ]}
        />
        <PolicyParagraph>
          This information is used to operate our website, improve performance,
          and understand general usage trends.
        </PolicyParagraph>
      </>
    ),
  },
  {
    id: "when-collected",
    heading: "When Information Is Collected",
    content: (
      <>
        <PolicyParagraph>We collect information when you:</PolicyParagraph>
        <PolicyList
          items={[
            "Fill out a contact or inquiry form",
            "Register through systems powered by Red Belt Gym",
            "Communicate with us by email or phone",
          ]}
        />
      </>
    ),
  },
  {
    id: "how-used",
    heading: "How We Use Your Information",
    content: (
      <>
        <PolicyParagraph>
          Superhero Jiu Jitsu Academy uses collected information to:
        </PolicyParagraph>
        <PolicyList
          items={[
            "Respond to inquiries and requests",
            "Follow up after form submissions or communication",
            "Provide service-related updates or relevant information",
          ]}
        />
        <PolicyParagraph>
          We do not sell, rent, or trade personal information to third parties.
        </PolicyParagraph>
      </>
    ),
  },
  {
    id: "data-security",
    heading: "Data Handling & Security",
    content: (
      <>
        <PolicyParagraph>
          Our website and forms are supported by Red Belt Gym software, operated
          by Red Belt Software. Personal information submitted through our site
          is protected using industry-standard security practices, including:
        </PolicyParagraph>
        <PolicyList
          items={[
            "Secure servers and restricted access",
            "Encrypted data transmission (SSL technology where applicable)",
            "Limited access to personal data by authorized personnel only",
            "Payment transactions, if applicable, are processed through secure third-party gateway providers and are not stored on our local servers.",
          ]}
        />
      </>
    ),
  },
  {
    id: "third-party",
    heading: "Third-Party Disclosure",
    content: (
      <>
        <PolicyParagraph>
          We do not sell or share your Personally Identifiable Information with
          outside parties, except:
        </PolicyParagraph>
        <PolicyParagraph>
          Trusted service providers (such as Red Belt Software) who assist in
          operating our website and systems and are required to keep information
          confidential.
        </PolicyParagraph>
        <PolicyParagraph>
          When disclosure is required to comply with the law or protect rights,
          property, or safety.
        </PolicyParagraph>
        <PolicyParagraph>
          Non-personally identifiable, aggregated data may be used for analytics
          or reporting purposes.
        </PolicyParagraph>
      </>
    ),
  },
  {
    id: "cookies",
    heading: "Cookies & Tracking",
    content: (
      <PolicyParagraph>
        Our website does not use cookies for direct tracking purposes. Some site
        features may rely on standard browser functionality. You can adjust
        cookie settings through your browser preferences.
      </PolicyParagraph>
    ),
  },
  {
    id: "children",
    heading: "Children's Information",
    content: (
      <PolicyParagraph>
        Superhero Jiu Jitsu Academy does not knowingly collect personal
        information online from children under the age of 18 without parental
        consent. Youth program inquiries are expected to be submitted by a
        parent or legal guardian.
      </PolicyParagraph>
    ),
  },
  {
    id: "rights",
    heading: "Your Rights & Choices",
    content: (
      <>
        <PolicyParagraph>You may request to:</PolicyParagraph>
        <PolicyList
          items={[
            "Review your personal information",
            "Update or correct your information",
            "Stop receiving communications",
          ]}
        />
        <PolicyParagraph>
          Requests can be made by contacting us directly using the information
          below.
        </PolicyParagraph>
      </>
    ),
  },
  {
    id: "deletion",
    heading: "Data Deletion Requests",
    content: (
      <>
        <PolicyParagraph>
          For data submitted through systems powered by Red Belt Gym, users may
          request deletion of their personal data.
        </PolicyParagraph>
        <PolicyParagraph>To request deletion:</PolicyParagraph>
        <ul className="mt-3 space-y-2">
          <li className="flex items-start gap-2.5 text-muted-foreground font-body text-base leading-relaxed">
            <span className="mt-1.5 w-2 h-2 rounded-full bg-gold flex-shrink-0" />
            Email:{" "}
            <a
              href="mailto:rbgsupport@redbeltsoftware.com"
              className="text-foreground font-medium hover:text-gold transition-colors duration-200 ml-1 break-all"
            >
              rbgsupport@redbeltsoftware.com
            </a>
          </li>
        </ul>
        <PolicyParagraph>
          Upon verification, associated personal data will be permanently
          deleted from Red Belt Gym systems within 30 days, except where
          retention is required by law or for legitimate business records.
        </PolicyParagraph>
      </>
    ),
  },
  {
    id: "updates",
    heading: "Policy Updates",
    content: (
      <PolicyParagraph>
        Any updates to this Privacy Policy will be posted on this page.
        Continued use of our website indicates acceptance of the updated policy.
      </PolicyParagraph>
    ),
  },
  {
    id: "contact",
    heading: "Contact Information",
    content: (
      <>
        <PolicyParagraph>
          If you have questions regarding this Privacy Policy or your personal
          information, please contact:
        </PolicyParagraph>
        <div className="mt-4 bg-muted/50 rounded-xl border border-border p-5 space-y-1.5">
          <p className="font-display font-semibold text-foreground text-base">
            Superhero Jiu Jitsu Academy
          </p>
          <p className="text-muted-foreground font-body text-sm">
            Springfield, OR (Lane County)
          </p>
          <p className="text-muted-foreground font-body text-sm">
            Email:{" "}
            <a
              href="mailto:shjjoffice@gmail.com"
              className="text-foreground font-medium hover:text-gold transition-colors duration-200"
            >
              shjjoffice@gmail.com
            </a>
          </p>
        </div>
      </>
    ),
  },
];

export default function PrivacyPolicyPage() {
  return (
    <Layout>
      <title>Privacy Policy | Superhero Jiu Jitsu MMA in Springfield, OR</title>

      {/* Hero banner */}
      <section className="bg-hero py-14 px-4 text-center">
        <motion.div className="container mx-auto max-w-2xl" {...fadeUp(0)}>
          <span className="inline-flex items-center gap-2 text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
            <Shield className="w-3.5 h-3.5" />
            Legal
          </span>
          <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight text-balance">
            Privacy Policy
          </h1>
          <p className="mt-4 text-hero-foreground font-body text-base max-w-lg mx-auto leading-relaxed opacity-90">
            We have developed this Policy in order for you to understand how we
            collect, use, communicate and disclose and make use of personal
            information.
          </p>
        </motion.div>
      </section>

      {/* Gold divider */}
      <div className="h-1 bg-gradient-accent" />

      {/* Policy content */}
      <section className="bg-background py-14 px-4">
        <div className="container mx-auto max-w-3xl">
          <div className="space-y-10">
            {sections.map((section, i) => (
              <motion.div
                key={section.id}
                id={section.id}
                {...fadeUp(i * 0.05)}
                className="bg-card rounded-2xl border border-border p-7 sm:p-8 shadow-card"
              >
                {/* Section accent bar */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-7 rounded-full bg-gradient-accent flex-shrink-0" />
                  <h2 className="font-display font-bold text-xl sm:text-2xl text-foreground">
                    {section.heading}
                  </h2>
                </div>
                <div className="pl-4">{section.content}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
